<template>
    <div v-if="resourceMenu" v-click-outside="() => closeResourcesMenu(null)" class="bg-white pt-35 pb-48 px-48 absolute z-1 right-0 top-full w-full max-w-800 shadow shadow-topBarSmallMenu">
        <div>
            <div class="c-page-header-resource-menu md:flex leading-double text-black oprema:text-opremablack">
                <ul v-for="(linkGroup, linkGroupIndex) in resourceMenu.resourceMenuItemGroups"
                    :key="linkGroupIndex"
                    class="pr-20 lg:pr-40"
                    :class="`md:w-1/${resourceMenu.resourceMenuItemGroups && resourceMenu.resourceMenuItemGroups.length || 3}`">
                    <li class="mb-15" @click="closeResourcesMenu(null)">
                        <CultureAwareRouterLink :to="linkGroup.groupUrl">
                            <ResponsiveIcon v-if="linkGroup.icon && linkGroup.icon.url"
                                            :image-url="linkGroup.icon.url"
                                            :alt-text="linkGroup.headline || ''"
                                            :height="40"
                                            :width="40"
                                            class="mr-7 w-20"/>
                            <span class="inline-block text-16 font-semi-bold">
                                {{ linkGroup.headline }}
                            </span>
                        </CultureAwareRouterLink>
                    </li>
                    <li v-for="(link, linkIndex) in linkGroup.links" :key="linkIndex">
                        <CultureAwareRouterLink :to="link.url"
                                                :target="link.target"
                                                @click="closeResourcesMenu(null)">
                            <span>
                                {{ link.name }}
                            </span>
                        </CultureAwareRouterLink>
                    </li>
                </ul>
            </div>
            <transition appear mode="out-in"
                        enter-active-class="animated fadeIn u-anim-dur-150">
                <AdnuntiusPixels v-if="adContentBlockViewModel"
                                 :rendered-url="renderedPixelUrl"
                                 :visible-url="visiblePixelUrl"
                                 :view-url="viewPixelUrl"
                                 :click-url="clickPixelUrl"
                                 :tracking-data="adnuntiusTrackingData">
                    <template #default="{ click }">
                        <CultureAwareRouterLink class="w-full flex mt-50"
                                                :to="adContentBlockViewModel.callToAction && adContentBlockViewModel.callToAction.url"
                                                :target="adContentBlockViewModel.callToAction && adContentBlockViewModel.callToAction.target"
                                                @click="adContentBlockViewModel.callToAction && adContentBlockViewModel.callToAction.url && closeResourcesMenu(click)">
                            <img v-if="adContentBlockViewModel.backgroundImages && adContentBlockViewModel.backgroundImages.length > 0"
                                 :src="adContentBlockViewModel.backgroundImages[0].url"
                                 class="max-w-250">
                            <div class="bg-black text-white hover:text-gray-400 py-20 px-32 flex flex-col flex-1 justify-center font-regular" :style="contentStyle">
                                <div v-if="adContentBlockViewModel.caption" class="text-12">
                                    {{ adContentBlockViewModel.caption }}
                                </div>
                                <div v-if="adContentBlockViewModel.header" class="font-semi-bold text-18 mb-7">
                                    {{ adContentBlockViewModel.header }}
                                </div>
                                <div v-if="adContentBlockViewModel.subheader" class="text-16 mb-20">
                                    {{ adContentBlockViewModel.subheader }}
                                </div>
                                <div v-if="adContentBlockViewModel.callToAction" class="text-16 underline" :style="callToActionStyle">
                                    {{ adContentBlockViewModel.callToAction.name }}
                                </div>
                            </div>
                        </CultureAwareRouterLink>
                    </template>
                </AdnuntiusPixels>
            </transition>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, StyleValue } from 'vue';
import { useRoute } from 'vue-router';
import { ResourceMenuViewModel } from '@/types/contentServerContract';
import serverContextService from '@/core/serverContext.service';
import adnuntiusService from '@/project/personalization/adnuntius.service';
import { ExtendedAdContentBlockViewModel } from '@/project/app/blocks/AdnuntiusViewModel';
import { AdnuntiusTrackingData } from '@/project/tracking/adnuntius/types';
import AdnuntiusPixels from '@/project/tracking/adnuntius/AdnuntiusPixels.vue';
import { hexToRgb, isValidHexColor } from '@/core/color/color.service';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwind from '@/styling/tailwind.config';
import ResponsiveIcon from '@/core/responsive/image/ResponsiveIcon.vue';

const tailwindConfiguration = resolveConfig(tailwind);
const route = useRoute();

const emit = defineEmits<{
    (e: 'close'): void;
}>();

const adContentBlockViewModel = ref<ExtendedAdContentBlockViewModel | null>(null);
const adnuntiusTrackingData = ref<AdnuntiusTrackingData | null>(null);

const resourceMenu = computed<ResourceMenuViewModel | null>(() => {
    return serverContextService.resourceMenu;
});

const renderedPixelUrl = computed(() => {
    return adContentBlockViewModel.value?.renderedPixelUrl;
});

const viewPixelUrl = computed(() => {
    return adContentBlockViewModel.value?.viewPixelUrl;
});

const visiblePixelUrl = computed(() => {
    return adContentBlockViewModel.value?.visiblePixelUrl;
});

const clickPixelUrl = computed(() => {
    return adContentBlockViewModel.value?.clickPixelUrl;
});

const contentStyle = computed<StyleValue>(() => {
    const style: StyleValue = {};

    if (adContentBlockViewModel.value) {
        if (adContentBlockViewModel.value.backgroundColor) {
            let result = 'transparent';
            if (isColorDefined(adContentBlockViewModel.value.backgroundColor)) {
                result = hexToRgbWithDefault(adContentBlockViewModel.value.backgroundColor);
            }
            style.backgroundColor = result;
        }

        if (adContentBlockViewModel.value.headerColor && isColorDefined(adContentBlockViewModel.value.headerColor)) {
            style.color = hexToRgbWithDefault(adContentBlockViewModel.value.headerColor);
        }
    }

    return style;
});

const callToActionStyle = computed<StyleValue>(() => {
    const style: StyleValue = {};

    if (adContentBlockViewModel.value?.callToActionColor && isColorDefined(adContentBlockViewModel.value.callToActionColor)) {
        style.color = hexToRgbWithDefault(adContentBlockViewModel.value.callToActionColor);
    }

    return style;
});

async function getAdContent(adUnitId: string) {
    try {
        const content: ExtendedAdContentBlockViewModel = await adnuntiusService.getBundledPersonalizedContent(
            adUnitId,
            route.fullPath
        );

        content.isBackgroundVideo = !!content.vimeoVideoId; // To make ImageTextVideoBlock work, it must have video playing in background

        // hide callToAction if both overlayVimeoCta and overlayVimeoVideoId is set
        if (content.overlayVimeoVideoId && content.overlayVimeoCta && content.callToAction) {
            content.callToAction = undefined;
        }

        adContentBlockViewModel.value = content;

        if (adContentBlockViewModel.value.lineItemId && adContentBlockViewModel.value.creativeId) {
            adnuntiusTrackingData.value = {
                lineItemId: adContentBlockViewModel.value.lineItemId,
                creativeId: adContentBlockViewModel.value.creativeId,
                parentBlockAlias: 'personalizedContentResourceMenu'
            };
        }
    } catch {}
}

function hexToRgbWithDefault(hex: string, alpha?: number): string {
    if (!isValidHexColor(hex)) return tailwindConfiguration.theme.colors.black;
    return hexToRgb(hex, alpha);
}

function isColorDefined(color: string = ''): boolean {
    return color?.length === 7; // #233445
}

function closeResourcesMenu(handler: Function | null = null) {
    if (handler) {
        handler.call(null);
    }
    emit('close');
}

onMounted(() => {
    if (resourceMenu.value?.personalizedContent?.selectedAdUnitIdContent?.adUnitId) {
        getAdContent(resourceMenu.value.personalizedContent.selectedAdUnitIdContent.adUnitId);
    }
});
</script>
